import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { postService } from '@services/api/posts'
import { categoryService } from '@services/api'
import toast from 'react-hot-toast'
import { useAuthStore } from '@store'
import { getCategoryFields } from '@constants/categoryFields'
import { CATEGORIES } from '@constants/categories'
import { storageURL, mediaUrl } from '@config'
import apiClient from '@services/api/client'

function EditPostPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [categories, setCategories] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [categoryFieldsConfig, setCategoryFieldsConfig] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    location: '',
    description: '',
    price: '',
    discount: '',
    delivery: 'gratuite',
    condition: '',
    images: [],
    imageFiles: [],
    imageLimit: 3,
    existingImages: [],
    existingImageIds: [],
    mainImageIndex: 0,
    categoryFields: {}
  })

  const discounts = [
    'Aucune réduction',
    '5%',
    '10%',
    '15%',
    '20%',
    '25%',
    '30%',
    '40%',
    '50%',
    '60%',
    '70%',
    '80%'
  ]

  // Charger les catégories depuis l'API (pour avoir l'ID de correspondance)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true)
        const result = await categoryService.getCategories()
        const data = result.data || []
        const apiCategories = data.filter(cat =>
          cat.libele_categorie !== 'Toutes les catégories'
        )
        setCategories(apiCategories)
      } catch (err) {
        console.error('Erreur lors du chargement des catégories:', err)
        toast.error('Impossible de charger les catégories')
      } finally {
        setLoadingCategories(false)
      }
    }
    fetchCategories()
  }, [])

  // Charger les données du post
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setIsLoading(true)
        const res = await postService.getPostById(id)
        const p = res.data || res  // unwrap { success, data } or direct response

        // Extraire le prix numérique
        const rawPrice = p.prix
        let price = ''
        if (rawPrice !== null && rawPrice !== undefined) {
          price = String(parseFloat(rawPrice) || rawPrice).replace(/\.0+$/, '')
        }

        // Récupérer le nom de la catégorie pour les champs dynamiques
        const categoryName = categories.find(
          cat => String(cat.categorie_id) === String(p.post_categorie_id)
        )?.libele_categorie

        // Champs spécifiques à la catégorie — lire depuis informations_specifiques (JSON) ou champs directs
        let categoryFieldsData = {}
        if (categoryName) {
          const fieldsConfig = getCategoryFields(categoryName)
          if (fieldsConfig) {
            setCategoryFieldsConfig(fieldsConfig)
            // 1. Essayer informations_specifiques (JSON stocké à la création)
            if (p.informations_specifiques) {
              try {
                const parsed = typeof p.informations_specifiques === 'string'
                  ? JSON.parse(p.informations_specifiques)
                  : p.informations_specifiques
                categoryFieldsData = { ...parsed }
              } catch {}
            }
            // 2. Fallback : chercher les champs directement dans l'objet post
            fieldsConfig.fields.forEach(field => {
              if (!categoryFieldsData[field.name] && p[field.name]) {
                categoryFieldsData[field.name] = p[field.name]
              }
            })
          }
        }

        // Déterminer le % de réduction pour pré-remplir le menu.
        // Convention : marge_reduite = %. Repli : ancienne convention (new_price = ancien prix).
        let discountValue = 'Aucune réduction'
        const rawNewPrice = parseFloat(p.new_price)
        const rawPrix = parseFloat(p.prix)
        const rawMarge = parseInt(String(p.marge_reduite ?? '').replace(/[^0-9]/g, '')) || 0
        let pct = 0
        if (rawMarge > 0) {
          pct = rawMarge
        } else if (rawNewPrice > rawPrix && rawNewPrice > 0) {
          pct = Math.round((1 - rawPrix / rawNewPrice) * 100)
        }
        if (pct > 0) {
          const found = discounts.find(d => d === `${pct}%`)
          discountValue = found || 'Aucune réduction'
        }

        // Construire les URLs des images existantes avec leur ID fichier
        const fichiers = p.fichiers || []
        const existingImages = fichiers.map(f => mediaUrl(f.chemin))
        const existingImageIds = fichiers.map(f => f.fichier_id)

        // Détecter l'index de l'image principale
        const mainIdx = fichiers.findIndex(f => f.is_main)
        const mainImageIndex = mainIdx >= 0 ? mainIdx : 0

        // S'assurer qu'une image est marquée comme principale
        if (mainIdx < 0 && fichiers.length > 0) {
          fichiers[0].is_main = true
        }

        setFormData(prev => ({
          ...prev,
          title:          p.libele       || '',
          category:       p.post_categorie_id ? String(p.post_categorie_id) : '',
          location:       p.siteWeb      || p.location || '',
          description:    p.description  || '',
          price,
          condition:      p.etat_appareil || p.condition || '',
          delivery:       p.livraison    || p.delivery  || 'gratuite',
          discount:       discountValue,
          images:         [],
          imageFiles:     [],
          imageLimit:     existingImages.length > 6 ? 9 : existingImages.length > 3 ? 6 : 3,
          existingImages,
          existingImageIds,
          mainImageIndex,
          categoryFields: categoryFieldsData,
        }))
      } catch (error) {
        console.error('Erreur lors du chargement du post:', error)
        toast.error('Impossible de charger le post')
        navigate('/dashboard')
      } finally {
        setIsLoading(false)
      }
    }

    if (id) {
      fetchPost()
    }
  }, [id, navigate, categories])
  
  // Gérer le changement de catégorie
  useEffect(() => {
    if (formData.category && categories.length > 0) {
      const categoryName = categories.find(cat => cat.categorie_id === parseInt(formData.category))?.libele_categorie
      if (categoryName) {
        const fields = getCategoryFields(categoryName)
        setCategoryFieldsConfig(fields)
        // Ne pas réinitialiser categoryFields lors de l'édition
      }
    } else {
      setCategoryFieldsConfig(null)
    }
  }, [formData.category, categories])

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files)
    const totalImages = formData.existingImages.length + formData.images.length + files.length
    
    if (totalImages <= formData.imageLimit) {
      const newImages = files.map(file => URL.createObjectURL(file))
      const newFiles = [...formData.imageFiles, ...files]
      setFormData({ 
        ...formData, 
        images: [...formData.images, ...newImages],
        imageFiles: newFiles
      })
    } else {
      toast.error(`Vous pouvez ajouter maximum ${formData.imageLimit} photos au total`)
    }
  }

  const handleImageLimitChange = (limit) => {
    const totalExisting = formData.existingImages.length
    const availableForNew = limit - totalExisting
    
    if (availableForNew >= 0) {
      const newImages = formData.images.slice(0, availableForNew)
      const newFiles = formData.imageFiles.slice(0, availableForNew)
      setFormData({ ...formData, imageLimit: limit, images: newImages, imageFiles: newFiles })
    } else {
      // Si on réduit la limite en dessous du nombre d'images existantes
      const keptExisting = formData.existingImages.slice(0, limit)
      setFormData({ 
        ...formData, 
        imageLimit: limit, 
        existingImages: keptExisting,
        images: [],
        imageFiles: []
      })
    }
  }

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index)
    const newFiles = formData.imageFiles.filter((_, i) => i !== index)
    setFormData({ ...formData, images: newImages, imageFiles: newFiles })
  }

  const removeExistingImage = (index) => {
    const newExistingImages = formData.existingImages.filter((_, i) => i !== index)
    const newExistingImageIds = formData.existingImageIds.filter((_, i) => i !== index)
    setFormData({ ...formData, existingImages: newExistingImages, existingImageIds: newExistingImageIds, mainImageIndex: 0 })
  }

  const setMainImage = (index) => {
    const newExistingImages = [...formData.existingImages]
    const newExistingImageIds = [...formData.existingImageIds]

    const [selectedImage] = newExistingImages.splice(index, 1)
    const [selectedId] = newExistingImageIds.splice(index, 1)

    newExistingImages.unshift(selectedImage)
    newExistingImageIds.unshift(selectedId)

    setFormData({ ...formData, existingImages: newExistingImages, existingImageIds: newExistingImageIds, mainImageIndex: 0 })
  }
  
  const handleCategoryFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      categoryFields: {
        ...prev.categoryFields,
        [fieldName]: value
      }
    }))
  }
  
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const totalImages = formData.existingImages.length + formData.images.length
    
    if (totalImages === 0) {
      toast.error('Veuillez garder au moins une photo')
      return
    }

    if (!formData.title || !formData.category || !formData.location || !formData.description || !formData.price) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    setIsSubmitting(true)

    try {
      let result

      const mainImageId = formData.existingImageIds[formData.mainImageIndex] ?? null

      if (formData.imageFiles.length > 0) {
        // Avec nouveaux fichiers : POST + _method=PUT (PHP ne parse pas multipart/PUT)
        const formDataToSend = new FormData()
        formDataToSend.append('_method', 'PUT')
        formDataToSend.append('libele', formData.title)
        formDataToSend.append('post_categorie_id', formData.category)
        formDataToSend.append('siteWeb', formData.location)
        formDataToSend.append('description', formData.description)
        formDataToSend.append('prix', formData.price)
        if (formData.condition) {
          formDataToSend.append('etat_appareil', formData.condition)
        }
        if (formData.discount && formData.discount !== 'Choisissez un champ') {
          const pct = parseInt(formData.discount)
          if (pct > 0 && pct < 100) {
            formDataToSend.append('new_price', Math.round(parseFloat(formData.price) * (1 - pct / 100)))
            formDataToSend.append('marge_reduite', pct)
          }
        }
        if (formData.delivery) {
          formDataToSend.append('livraison', formData.delivery)
        }
        // Champs spécifiques à la catégorie — envoyés comme colonnes directes
        if (formData.categoryFields) {
          Object.entries(formData.categoryFields).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              formDataToSend.append(key, Array.isArray(value) ? JSON.stringify(value) : value)
            }
          })
        }
        if (mainImageId !== null) {
          formDataToSend.append('main_image_id', mainImageId)
        }
        formData.imageFiles.forEach(file => formDataToSend.append('files[]', file))
        result = await apiClient.post(`/posts/${id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      } else {
        // Sans fichier : PUT JSON standard
        result = await apiClient.put(`/posts/${id}`, {
          libele:                    formData.title,
          post_categorie_id:         formData.category,
          siteWeb:                   formData.location,
          description:               formData.description,
          prix:                      formData.price,
          ...(formData.condition && { etat_appareil: formData.condition }),
          ...(formData.discount && formData.discount !== 'Choisissez un champ' && (() => {
            const pct = parseInt(formData.discount)
            return pct > 0 && pct < 100
              ? { new_price: Math.round(parseFloat(formData.price) * (1 - pct / 100)), marge_reduite: pct }
              : {}
          })()),
          ...(formData.delivery && { livraison: formData.delivery }),
          // Champs spécifiques à la catégorie — colonnes directes
          ...(formData.categoryFields && Object.fromEntries(
            Object.entries(formData.categoryFields)
              .filter(([, v]) => v !== undefined && v !== null && v !== '')
              .map(([k, v]) => [k, Array.isArray(v) ? JSON.stringify(v) : v])
          )),
          ...(mainImageId !== null && { main_image_id: mainImageId }),
        })
      }

      if (result.success) {
        toast.success('Annonce mise à jour avec succès !')
        setTimeout(() => navigate('/dashboard'), 1500)
      } else {
        throw new Error(result.message || 'Erreur lors de la mise à jour')
      }
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'annonce:', error)
      const errorMessage = error.message || 'Une erreur est survenue lors de la mise à jour. Veuillez réessayer.'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fefcf8] flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#D4AF37] mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#fefcf8]">
      <div className="w-full md:w-[90%] lg:w-[80%] mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 lg:p-12">
          {/* En-tête */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#001F3F] mb-2">Modifier l'annonce</h1>
            <p className="text-gray-600">Modifiez les informations de votre annonce</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Colonne gauche */}
              <div className="space-y-6">
                {/* Titre */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Titre<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Veuillez écrire un titre clair pour votre article"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                    required
                  />
                </div>

                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Catégorie<span className="text-red-600">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                    required
                  >
                    <option value="">Sélectionnez une catégorie</option>
                    {loadingCategories ? (
                      <option disabled>Chargement...</option>
                    ) : (
                      categories.map((cat) => (
                        <option key={cat.categorie_id} value={cat.categorie_id}>
                          {cat.libele_categorie}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {/* Situation géographique */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Situation géographique<span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Votre emplacement"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    Description<span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Décrivez votre article"
                    rows="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm resize-none"
                    required
                  />
                </div>

                {/* État */}
                <div>
                  <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                    État<span className="text-red-600">*</span>
                  </label>
                  <select
                    name="condition"
                    value={formData.condition}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      backgroundSize: '1.5em 1.5em'
                    }}
                    required
                  >
                    <option value="">Sélectionnez l'état</option>
                    <option value="neuf">Neuf</option>
                    <option value="scelle">Scellé</option>
                    <option value="reconditionne">Reconditionné</option>
                    <option value="quasi_neuf">Quasi neuf</option>
                    <option value="excellent">Excellent</option>
                    <option value="bon">Bon état</option>
                    <option value="moyen">État moyen</option>
                    <option value="usage">Usagé</option>
                  </select>
                </div>
                
                {/* Champs spécifiques à la catégorie */}
                {categoryFieldsConfig && (
                  <div className="col-span-2 mt-6">
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                      <h3 className="text-lg font-bold text-[#001F3F] mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Informations spécifiques - {categories.find(cat => cat.categorie_id === parseInt(formData.category))?.libele_categorie}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {categoryFieldsConfig.fields.map((field) => (
                          <div key={field.name} className={field.type === 'checkbox' ? 'md:col-span-2' : ''}>
                            <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                              {field.label}
                              {field.required && <span className="text-red-600">*</span>}
                            </label>
                            
                            {field.type === 'select' && (
                              <select
                                value={formData.categoryFields[field.name] || ''}
                                onChange={(e) => handleCategoryFieldChange(field.name, e.target.value)}
                                required={field.required}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                                style={{
                                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                  backgroundRepeat: 'no-repeat',
                                  backgroundPosition: 'right 0.75rem center',
                                  backgroundSize: '1.5em 1.5em'
                                }}
                              >
                                <option value="">Sélectionnez...</option>
                                {field.options.map((option) => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            )}
                            
                            {(field.type === 'text' || field.type === 'number') && (
                              <div className="relative">
                                <input
                                  type={field.type}
                                  value={formData.categoryFields[field.name] || ''}
                                  onChange={(e) => handleCategoryFieldChange(field.name, e.target.value)}
                                  placeholder={field.placeholder}
                                  required={field.required}
                                  min={field.min}
                                  max={field.max}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                                />
                                {field.suffix && (
                                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                                    {field.suffix}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {field.type === 'checkbox' && (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {field.options.map((option) => (
                                  <label key={option} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={(formData.categoryFields[field.name] || []).includes(option)}
                                      onChange={(e) => {
                                        const current = formData.categoryFields[field.name] || []
                                        const updated = e.target.checked
                                          ? [...current, option]
                                          : current.filter(item => item !== option)
                                        handleCategoryFieldChange(field.name, updated)
                                      }}
                                      className="w-4 h-4 text-[#D4AF37] border-gray-300 rounded focus:ring-[#D4AF37]"
                                    />
                                    <span className="text-sm text-gray-700">{option}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Colonne droite */}
              <div className="space-y-6">
                {/* Modifier les photos */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-semibold text-[#001F3F]">
                      Modifier les photos
                    </label>
                    {/* Sélecteur de nombre d'images */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleImageLimitChange(3)}
                        className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-colors ${
                          formData.imageLimit === 3
                            ? 'bg-[#D4AF37] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        3
                      </button>
                      <button
                        type="button"
                        onClick={() => handleImageLimitChange(6)}
                        className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-colors ${
                          formData.imageLimit === 6
                            ? 'bg-[#D4AF37] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        6
                      </button>
                      <button
                        type="button"
                        onClick={() => handleImageLimitChange(9)}
                        className={`px-3 py-1.5 rounded-lg font-medium text-xs transition-colors ${
                          formData.imageLimit === 9
                            ? 'bg-[#D4AF37] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        9
                      </button>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mb-3">
                    Cliquez sur une image pour la définir comme image principale. {formData.existingImages.length + formData.images.length}/{formData.imageLimit}
                  </p>
                  
                  <div className="grid grid-cols-3 gap-2">
                    {/* Images existantes */}
                    {formData.existingImages.map((img, index) => (
                      <div key={img} className="relative aspect-square border-2 border-gray-200 rounded-lg overflow-hidden group bg-white">
                        <img src={img} alt={`Image ${index + 1}`} className="w-full h-full object-cover" />
                        
                        {/* Badge Image Principale */}
                        {formData.mainImageIndex === index && (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-[#D4AF37] text-white text-xs rounded-md font-bold shadow-lg z-10 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            Principale
                          </div>
                        )}
                        
                        {/* Bouton Supprimer */}
                        <button
                          type="button"
                          onClick={() => removeExistingImage(index)}
                          className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 text-lg font-bold z-10 shadow-lg"
                          title="Supprimer l'image"
                        >
                          ×
                        </button>
                        
                        {/* Bouton Définir comme principale */}
                        {formData.mainImageIndex !== index && (
                          <button
                            type="button"
                            onClick={() => setMainImage(index)}
                            className="absolute bottom-2 left-2 right-2 px-2 py-1.5 bg-white/95 hover:bg-white text-[#001F3F] text-xs rounded-md font-semibold opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10 flex items-center justify-center gap-1"
                            title="Définir comme image principale"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                            Principale
                          </button>
                        )}
                        
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-1">
                          <span className="text-white text-xs font-medium">Image {index + 1}</span>
                        </div>
                      </div>
                    ))}

                    {/* Nouvelles images uploadées */}
                    {formData.images.map((img, index) => (
                      <div key={`new-${index}`} className="relative aspect-square border-2 border-blue-400 rounded-lg overflow-hidden group bg-white">
                        <img src={img} alt={`Nouvelle ${index + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute top-1 left-1 px-2 py-0.5 bg-blue-500 text-white text-xs rounded font-medium">
                          Nouveau
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 text-lg font-bold"
                        >
                          ×
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-1">
                          <span className="text-white text-xs font-medium">Image {formData.existingImages.length + index + 1}</span>
                        </div>
                      </div>
                    ))}
                    
                    {/* Boutons d'upload restants */}
                    {[...Array(Math.max(0, formData.imageLimit - formData.existingImages.length - formData.images.length))].map((_, index) => (
                      <label
                        key={`upload-${index}`}
                        className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all bg-gray-50"
                      >
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          multiple
                        />
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span className="text-xs text-gray-400 mt-1">Ajouter</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Section Prix et Réduction */}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Prix */}
              <div>
                <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                  Prix<span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="Prix en FCFA"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm"
                  required
                />
              </div>

              {/* Appliquer une réduction */}
              <div>
                <label className="block text-sm font-semibold text-[#001F3F] mb-2">
                  Appliquer une réduction
                </label>
                <select
                  name="discount"
                  value={formData.discount}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent text-sm appearance-none bg-white"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23001F3F'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.5em 1.5em'
                  }}
                >
                  {discounts.map((discount, index) => (
                    <option key={index} value={discount}>{discount}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Livraison */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-[#001F3F] mb-3">
                Livraison<span className="text-red-600">*</span>
              </label>
              <div className="flex items-center gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="delivery"
                    value="gratuite"
                    checked={formData.delivery === 'gratuite'}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#D4AF37] border-gray-300 focus:ring-[#D4AF37]"
                  />
                  <span className="ml-2 text-sm text-[#001F3F]">Gratuite</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="delivery"
                    value="payante"
                    checked={formData.delivery === 'payante'}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#D4AF37] border-gray-300 focus:ring-[#D4AF37]"
                  />
                  <span className="ml-2 text-sm text-[#001F3F]">Payante</span>
                </label>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 sticky bottom-0 bg-white py-4 -mx-8 px-8 lg:-mx-12 lg:px-12">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-8 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors min-w-[140px]"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-8 py-3 bg-[#D4AF37] text-white font-semibold rounded-lg hover:bg-[#c09d2a] transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Mise à jour...
                  </span>
                ) : (
                  'Enregistrer les modifications'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditPostPage
